import React from 'react';
import { toDisplayHandicap, formatHandicap } from '../utils/pingolfScoring';
import './PinGolfHandicapTrend.css';

// One line, one question: has my handicap been going up or down. Plotted in the SAME units
// the scorecard uses - strokes applied to your card, i.e. toDisplayHandicap(index) - so the
// value under the last dot is the number in the panel above it. Plotting the raw stored index
// instead would put a line on screen that runs the opposite way to every number beside it.
//
// Deliberately not a charting library: this is one series of at most a season's worth of
// points inside a bottom sheet, and the ledger underneath is the table view of the same data,
// so it needs a line, a baseline, two labels, and per-point hit targets - nothing else.

const VIEW_W = 320;
const VIEW_H = 130;
const PAD_L = 8;
const PAD_R = 34; // room for the trailing value label
const PAD_T = 14;
const PAD_B = 22; // room for the first/last date captions

const shortDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function PinGolfHandicapTrend({ entries }) {
  // Every point the handicap has ever been, oldest first. The first entry's "before" value is
  // included so a player with a single card still gets a line rather than a lone dot.
  const points = [];
  const first = entries[0];
  if (first && first.previousIndex !== null && first.previousIndex !== undefined) {
    points.push({ at: first.at, value: toDisplayHandicap(first.previousIndex), label: 'Starting point', isStart: true });
  }
  entries.forEach((entry) => {
    if (entry.newIndex === null || entry.newIndex === undefined) return;
    points.push({
      at: entry.at,
      value: toDisplayHandicap(entry.newIndex),
      label: entry.kind === 'card'
        ? `Card ${entry.cardNumber ?? ''}`.trim()
        : 'Seed change'
    });
  });

  if (points.length < 2) return null;

  const values = points.map((point) => point.value);
  const rawMin = Math.min(...values, 0);
  const rawMax = Math.max(...values, 0);
  // A flat history has zero range, which would divide by zero and pin every point to one edge.
  const span = rawMax - rawMin || 2;
  const min = rawMin - span * 0.15;
  const max = rawMax + span * 0.15;

  const x = (index) => PAD_L + (index / (points.length - 1)) * (VIEW_W - PAD_L - PAD_R);
  const y = (value) => PAD_T + (1 - (value - min) / (max - min)) * (VIEW_H - PAD_T - PAD_B);

  const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)},${y(point.value).toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  const zeroY = y(0);
  const showZero = min < 0 && max > 0;

  // Which way "up" reads: the plotted number is strokes applied to your card, and the league
  // spots the field for a strong player, so a RISING line means your scoring average improved
  // and the allowance you hand out grew. Saying it the other way round was the whole reason
  // players could not follow their own handicap.
  const direction = last.value - points[0].value;
  const caption = Math.abs(direction) < 0.05
    ? 'Flat across your whole history.'
    : direction > 0
      ? `Up ${Math.abs(direction).toFixed(1)} strokes since your first card - your scoring average has improved, so you spot the field more.`
      : `Down ${Math.abs(direction).toFixed(1)} strokes since your first card - recent cards have been worse than your early ones, so you spot the field less.`;

  return (
    <figure className="pg-hcp-trend">
      <svg
        className="pg-hcp-trend__svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="img"
        aria-label={`Your handicap over ${points.length} points, from ${formatHandicap(-points[0].value)} to ${formatHandicap(-last.value)}.`}
      >
        {/* Par. Only drawn when the line actually crosses it, so it never reads as an axis
            floating off to one side of the data. */}
        {showZero && (
          <>
            <line className="pg-hcp-trend__zero" x1={PAD_L} y1={zeroY} x2={VIEW_W - PAD_R} y2={zeroY} />
            <text className="pg-hcp-trend__zero-label" x={VIEW_W - PAD_R + 4} y={zeroY + 3}>0</text>
          </>
        )}

        <path className="pg-hcp-trend__line" d={path} />

        {points.map((point, index) => (
          <g key={`${point.at}-${index}`}>
            <circle
              className={`pg-hcp-trend__dot ${index === points.length - 1 ? 'is-last' : ''}`}
              cx={x(index)}
              cy={y(point.value)}
              r={index === points.length - 1 ? 4 : 2.5}
            />
            {/* The hit target is bigger than the mark, so a thumb can find it. */}
            <circle className="pg-hcp-trend__hit" cx={x(index)} cy={y(point.value)} r={9}>
              <title>{`${point.label} · ${shortDate(point.at)} · ${formatHandicap(-point.value, { decimals: 1 })}`}</title>
            </circle>
          </g>
        ))}

        {/* Only the current value is labelled. A number on every point turns the line into a
            table, and the real table is directly below this. */}
        <text className="pg-hcp-trend__value" x={x(points.length - 1) + 8} y={y(last.value) + 4}>
          {formatHandicap(-last.value, { decimals: 1 })}
        </text>

        <text className="pg-hcp-trend__axis" x={PAD_L} y={VIEW_H - 6}>{shortDate(points[0].at)}</text>
        <text className="pg-hcp-trend__axis is-end" x={VIEW_W - PAD_R} y={VIEW_H - 6}>{shortDate(last.at)}</text>
      </svg>
      <figcaption className="pg-hcp-trend__caption">{caption}</figcaption>
    </figure>
  );
}

export default PinGolfHandicapTrend;
